












































































































































































































import Vue, { PropOptions } from 'vue';
import chunk from 'lodash/chunk';
import get from 'lodash/get';
import SPagination from '../SPagination/SPagination.vue';
import SAlert from '../SAlert/SAlert.vue';
import SCircularLoader from '../SCircularLoader/SCircularLoader.vue';
import SelectAllBanner from './components/SelectAllBanner/SelectAllBanner.vue';
import STableFilter from './components/STableFilter.vue';
import STableHeader, { HeaderProps } from './components/STableHeader.vue';
import { TableHeader } from './STable.interface';
import sortAZ from '../../utils/sortAZ';

export default Vue.extend({
  name: 'STable',
  components: {
    SAlert,
    SPagination,
    SCircularLoader,
    SelectAllBanner,
    STableFilter,
    STableHeader,
  },
  props: {
    /**
     * The data that the table requires. If not using `dynamicData` mode provide all the data in one
     * array. Else provide just the contents of the page as an array
     */
    data: {
      type: Array,
      required: true,
    } as PropOptions<any[]>,
    /**
     * The property name from the item to use as the v-for key
     */
    itemKey: {
      type: String,
      required: false,
      validator(value): boolean {
        if (!value) {
          console.warn('STable: Setting an item-key is recommended');
        }
        return !!value;
      },
    },
    /**
     * The table headers
     */
    headers: {
      type: Array,
      required: true,
    } as PropOptions<TableHeader[]>,
    /**
     * If the data provided is loaded seperately for every page
     */
    dynamicData: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the component should show a loading symbol
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the component should show a loading placeholder when loading is true
     */
    loadingPlaceholder: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * A list of the 'Selected' row item keys
     */
    value: {
      type: Array,
      required: false,
    } as PropOptions<any[]>,
    /**
     * The total number of items in the data set. Is only needed when dynamicData is set to true
     */
    numberOfItems: {
      type: Number,
      required: false,
    },
    /**
     * The page size options for the pagination eg [5, 10, 25]
     */
    pageSizeOptions: {
      type: Array,
      required: false,
    } as PropOptions<number[]>,
    /**
     * If checkboxes should be shown in the row and header
     */
    showCheckboxes: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If rowClick should also check checkboxes
     */
    checkOnRowClick: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the filter should be shown
     */
    showFilter: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The function used to filter table items on the client side. If allowing the server to filter
     * then this is not required.
     */
    filterFn: {
      type: Function,
      required: false,
      default: (): boolean => true,
    } as PropOptions<(row: any, filter: any) => boolean>,
    /**
     * The label to be shown in the filter
     */
    filterLabel: {
      type: String,
      required: false,
      default: 'Search…',
    },
    /**
     * Inverts the colours, dark background with light text
     */
    dark: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The rows that should be higlighted. Can also set highlighted as true on the row
     */
    highlightedRowKeys: {
      type: Array,
      required: false,
      default: (): string[] => [],
    } as PropOptions<string[]>,
    /**
     * If you want to override the pagination handled by the component. Use the
     * [sync](https://vuejs.org/v2/guide/components-custom-events.html#sync-Modifier) modifier with
     * this prop.
     *
     * Default pagination:
     *
     * `{
     *   currentPage: 0,
     *   pageSize: 5,
     *   sortKey: '',
     *   sortDescending: true,
     * }`
     */
    pagination: {
      type: Object,
      required: false,
      default: (): {} => ({}),
    },
    /**
     * If the component should hide the pagination. When set to true, dummy rows are are removed
     */
    hidePagination: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the table should add dummy rows so that it has the same number of items as the
     * pageSize setting
     */
    shouldAddDummyRows: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Allows you to expanded a table row on load, rather than requiring the user to click a row
     * Pass the rows corresponding 'itemKey' value
     */
    expandedRow: {
      type: [String, Number],
      required: false,
    },
    /**
     * By default, expanded rows will open on row click. This disables this
     * You can still 'roll your own' expanding row logic by pushing to 'expandedRows' prop
     */
    disableExpandingRows: {
      type: Boolean,
      default: false,
    },
    /**
     * Reduce the height of table rows
     */
    dense: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // One day it may be possible to open multiple at once,
      expandedRows: this.expandedRow ? [this.expandedRow] : [] as any[],
      filter: '',
      allowSelectAllBanner: false, // NOTE: This is currently disabled if dynamicData is set to true
      selectAllBannerSelected: false,
      selectAllBannerMode: 'ADD',
      defaultPagination: {
        currentPage: 0,
        pageSize: 5,
        sortKey: '',
        sortDescending: false,
      },
    };
  },
  computed: {
    computedPagination(): any {
      return this.hasPaginationProp
        ? this.pagination
        : this.defaultPagination;
    },
    hasPaginationProp(): boolean {
      return this.pagination != null && Object.keys(this.pagination).length !== 0;
    },
    hasExpansionSlot(): boolean {
      return !!this.$scopedSlots.expanded;
    },
    /**
     * All the table items, only for tables without dynamicData
     */
    allItems(): any {
      if (this.dynamicData) return null;
      if (!this.filter) return this.data;
      const filterFn = (row) => this.filterFn(row, this.filter);
      return this.data.filter(filterFn);
    },
    tableItemsCount(): number {
      return this.dynamicData ? this.numberOfItems : this.allItems.length;
    },
    pages(): false | any[] {
      if (this.dynamicData) return false;
      const { sortKey, sortDescending, pageSize } = this.computedPagination;
      const diff = this.tableItemsCount % pageSize;
      const dummyCount = diff === 0 && this.tableItemsCount !== 0
        ? 0
        : pageSize - diff;

      const paddedItems = this.addDummyRows(
        this.sortAZ(this.allItems, sortKey, sortDescending),
        dummyCount,
      );
      return chunk(paddedItems, this.computedPagination.pageSize);
    },
    page(): any[] {
      /**
       * Makes sure the placeholders are showing when both the props are showing. Otherwise would
       * only show when data is an empty array
       */
      if (this.loading && this.loadingPlaceholder) {
        return this.addDummyRows([], this.computedPagination.pageSize);
      }

      if (!this.dynamicData) return this.pages[this.computedPagination.currentPage];
      const diff = this.data.length % this.computedPagination.pageSize;
      const dummyCount = diff === 0 && this.data.length !== 0
        ? 0
        : this.computedPagination.pageSize - diff;
      return this.addDummyRows([...this.data], dummyCount);
    },
    /**
     * The number of rows on the current pagination page
     */
    noValidPageItems(): number {
      return this.page.filter((row) => !row.isDummy).length;
    },
    /**
     * How many items on the current pagination page are selected?
     * Used to check the 'select all' checkbox
     */
    pageSelectedCount(): 'all' | 'none' | 'some' {
      const { page, value, itemKey } = this;

      if (!this.showCheckboxes || !page) return 'none';

      if (!value) {
        this.warnAboutValueProp();
        return 'none';
      }

      if (page.every((row) => value.includes(get(row, itemKey)) || row.isDummy)) {
        return 'all';
      }

      if (page.some((row) => value.includes(get(row, itemKey)))) {
        return 'some';
      }

      return 'none';
    },
    headerProps(): HeaderProps {
      const self = this;
      return {
        indeterminate: this.pageSelectedCount === 'some',
        checkBoxLabel: this.value ? `${this.value.length} / ${this.tableItemsCount}` : '',
        get allVModel() {
          return self.pageSelectedCount === 'all';
        },
        set allVModel(selected: boolean) {
          if (!self.value) {
            self.warnAboutValueProp();
            return;
          }

          if (!selected) {
            // Unselect ALL checkboxes on the current page by emitting only the values on another
            // page
            const checkedItemsNotOnThisPage = self.value.filter((id) => {
              const item = self.page.find((row) => get(row, self.itemKey) === id);
              return item == null;
            });
            self.$emit('input', checkedItemsNotOnThisPage);

            self.allowSelectAllBanner = checkedItemsNotOnThisPage.length !== 0;
          } else {
            // Select ALL checkboxes
            const newSelectedRows = self.page
              .map((row) => {
                if (row.isDummy) return false;
                return get(row, self.itemKey);
              })
              .filter((val) => val !== false);

            const allSelectedVessels = [...new Set([
              ...self.value,
              ...newSelectedRows,
            ])];

            /**
             * @event input
             * @type {array} An array of selected items' keys
             */
            self.$emit('input', allSelectedVessels);

            if (allSelectedVessels.length !== self.tableItemsCount) {
              self.allowSelectAllBanner = true;
            } else {
              self.allowSelectAllBanner = false;
            }
          }
          self.selectAllBannerMode = selected === true ? 'ADD' : 'REMOVE';
        },
      };
    },
    showSelectAllBanner(): boolean {
      /**
       * dynamicData mode doesn't currently support showing the select all banner
       */
      if (this.dynamicData) {
        return false;
      }

      if (!this.page) return false;

      if (!this.value) return false;

      /**
       * If the user unticks a row, hide the banner
       */
      const currentPageSelected = this.page.filter((row) => this.value.includes(row[this.itemKey]));
      if (currentPageSelected.length !== this.noValidPageItems) {
        return false;
      }

      return this.allowSelectAllBanner;
    },
    shouldShowTableRows(): boolean {
      return this.loading || this.tableItemsCount !== 0;
    },
    /**
     * Props passed to the before-filters & after-filters slots
     */
    filterProps(): any {
      const {
        sortKey,
        pageSize,
        currentPage,
        sortDescending,
      } = this.computedPagination;

      return {
        sortKey,
        pageSize,
        currentPage,
        sortDescending,
        filter: this.filter,
      };
    },
  },
  created() {
    if (this.computedPagination.sortKey !== '') return;
    this.updatePagination({
      sortKey: this.headers.length === 0 ? this.itemKey : this.headers[0].key,
    });
  },
  methods: {
    // The component won't use this fn if dynamicData is true
    sortAZ,
    lodashGet: get,
    /**
     * Method to be called by parent component to get all the params needed for it to make an api
     * request. See `Server side data` example in README on how to use.
     * @public
     * @returns {object}
     */
    getQueryParams() {
      return {
        page: this.page,
        filter: this.filter,
        sortKey: this.computedPagination.sortKey,
        sortDescending: this.computedPagination.sortDescending,
        currentPage: this.computedPagination.currentPage,
        pageSize: this.computedPagination.pageSize,
      };
    },
    changeSorting(key) {
      const { sortKey, sortDescending } = this.computedPagination;
      const currentlySorted = key === sortKey;

      this.updatePagination({
        sortKey: key,
        sortDescending: currentlySorted ? !sortDescending : false,
      });

      this.closeSelectAllBanner();
      /**
       * @event change:sorting
       * @type {object} Emitted when the sorting is changed
       * @property {string} sortKey The key being sorted by
       * @property {boolean} sortDescending Which way the items are being ordered
       */
      this.$emit('change:sorting', {
        sortKey: this.computedPagination.sortKey,
        sortDescending: this.computedPagination.sortDescending,
      });
    },
    getRowProps(row, index: number) {
      const self = this;
      const { itemKey, value } = this;

      // If an item key hasn't been defined use the index of the row as the key
      const id = get(row, itemKey, index);

      return {
        // 'Selected' means, checkbox is checked
        get selected() {
          return value ? value.includes(id) : false;
        },
        set selected(isSelected) {
          if (!value) {
            self.warnAboutValueProp();
            return;
          }
          const newValues = [...self.value];
          if (isSelected) newValues.push(id);
          else newValues.splice(newValues.indexOf(id), 1);
          self.$emit('input', newValues);
        },
        expanded: this.expandedRows.includes(id),
      };
    },
    getRowClassName(row) {
      const id = get(row, this.itemKey);
      return {
        's-table__row': true,
        's-table__row--dummy': row.isDummy,
        's-table__row--highlighted': row.highlighted || this.highlightedRowKeys.includes(id),
      };
    },
    onRowClick(row, event): void {
      if (row.isDummy) return;
      const id = get(row, this.itemKey);

      if (!this.disableExpandingRows) {
        this.expandedRows = this.expandedRows.includes(id) ? [] : [id];
      }

      /**
       * @event rowClick
       * @type {object} The row clicked
       */
      this.$emit('rowClick', row);

      /**
       * Checks the checkbox on relevant row
       */
      if (this.checkOnRowClick) {
        const targetIsCheckbox = event.target.previousSibling
          && event.target.previousSibling.type
          && event.target.previousSibling.type === 'checkbox';

        if (!targetIsCheckbox) {
          const checked = this.value;
          const checkedIndex = checked.indexOf(id);
          const alreadyChecked = checkedIndex >= 0;

          if (alreadyChecked) {
            checked.splice(checkedIndex, 1);
          } else {
            checked.push(id);
          }

          this.$emit('input', this.value);
        }
      }
    },
    onRowMouseover(row): void {
      if (row.isDummy) return;
      /**
       * @event rowHover
       * @type {object} The row hovered over
       */
      this.$emit('rowHover', row);
    },
    updatePagination(val) {
      const updatedPagination = {
        ...this.computedPagination,
        ...val,
      };

      if (!this.hasPaginationProp) {
        this.defaultPagination = updatedPagination;
      }

      /**
       * Emits when the pagination changes
       * @event update:pagination
       * @type {object}
       * @property {number} currentPage
       * @property {number} pageSize
       * @property {string} sortKey
       * @property {boolean} sortDescending
       */
      this.$emit('update:pagination', updatedPagination);
    },
    sPaginationUpdate(value) {
      const diffPageSize = value.pageSize !== this.computedPagination.pageSize;
      const diffCurrentPage = value.currentPage !== this.computedPagination.currentPage;
      if (!diffPageSize && !diffCurrentPage) return;
      this.updatePagination(value);

      if (this.selectAllBannerSelected) this.$emit('input', []);
      this.closeSelectAllBanner();

      if (diffCurrentPage) {
        /**
         * Emits when the currentPage changes
         * @event change:current-page
         * @type {number} The currently selected page
         */
        this.$emit('change:current-page', value.currentPage);
      }

      if (diffPageSize) {
        /**
         * Emits when the pageSize changes
         * @event change:page-size
         * @type {number} The current pageSize
         */
        this.$emit('change:page-size', value.pageSize);
      }
    },
    /**
     * Adds dummy rows to the table. Helps stop layout jumps when changing page
     */
    addDummyRows(rows, numberToAdd): object[] {
      if (!this.shouldAddDummyRows) return rows;

      // If the pageSize is Infinity (All) don't add any rows
      if (numberToAdd === Infinity) return rows;

      // If we aren't showing more than one page, don't add dummy rows
      if (this.hidePagination) return rows;

      return rows.concat(
        Array.from(new Array(numberToAdd)).map(() => ({
          isDummy: true,
        })),
      );
    },
    filterChange(value) {
      this.filter = value;

      this.sPaginationUpdate({
        currentPage: 0,
        pageSize: this.computedPagination.pageSize,
      });
      /**
       * @event change:filter
       * @type {string} The filter value
       */
      this.$emit('change:filter', value);
    },
    closeSelectAllBanner() {
      this.allowSelectAllBanner = false;
      this.selectAllBannerSelected = false;
    },
    warnAboutValueProp(): void {
      console.warn('The value/v-model prop is required when using checkboxes');
    },
    /**
     * Takes the filter function set in the table header config and applies it to the value.
     * e.g. dateTime or dashEmpty
     */
    applyValueFilter(row, column) {
      const val = this.lodashGet(row, column.key);
      if (typeof column.filter === 'function') {
        return column.filter(val);
      }
      return val;
    },
  },
});
